import { z } from 'zod';

// Where nurses request a pre-authorisation from the funder. Interim forms
// outside the EBP until the request is a screen of its own (a Claim in the
// Entitlements design, PRM-68).
export enum PreAuthorizationRequestForm {
  // no request link
  none = 'none',
  // the L&G Google Form, prefilled with the nurse, GIP code, level and case
  // manager; its URL and field ids are build env (REACT_APP_PREAUTHORIZATION_FORM_*)
  landgGoogleForm = 'landg-google-form',
  // requestFormUrl with the member ID (patient.shortId) as the bare hash and
  // nothing else: Vitality's Claude form looks the member up itself
  memberIdLink = 'member-id-link',
}

export function displayPreAuthorizationRequestForm(
  value: PreAuthorizationRequestForm,
) {
  switch (value) {
    case PreAuthorizationRequestForm.none:
      return 'None';
    case PreAuthorizationRequestForm.landgGoogleForm:
      return 'L&G Google Form, prefilled';
    case PreAuthorizationRequestForm.memberIdLink:
      return 'Form link with the member ID';
    default:
      throw new Error(`Unknown pre-authorisation request form: ${value}`);
  }
}

// Pre-authorisation: asking the funder for more, and recording the answer.
// Shown in the case's Support Authorisation section, which the support-level
// model (supportLevels) turns on.
// - requestForm / requestFormUrl: where the request is made. The URL is kept
//   (inert) when another form is chosen, and required for member-id-link.
// - recordsOutcome: the funder's answer is recorded in the EBP (Approved /
//   Rejected / Not required, and the authorisation log). Off when it is
//   recorded elsewhere (Vitality: Account Management's tracker).
export const PreAuthorizationSettingsSchema = z
  .strictObject({
    requestForm: z.enum(PreAuthorizationRequestForm),
    requestFormUrl: z.union([z.literal(''), z.url({ protocol: /^https$/ })]),
    recordsOutcome: z.boolean(),
  })
  .check((ctx) => {
    const { requestForm, requestFormUrl } = ctx.value;

    if (
      requestForm === PreAuthorizationRequestForm.memberIdLink &&
      requestFormUrl === ''
    ) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['requestFormUrl'],
        message: 'requestFormUrl is required for the member-id-link form',
      });
    }
  });

export type PreAuthorizationSettings = z.infer<
  typeof PreAuthorizationSettingsSchema
>;
